'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../utils/axios-config';
import { API_ENDPOINTS } from '../config/api-config';
import { notify } from '../stores';

/**
 * Custom hook for managing user notifications
 * Uses TanStack Query for caching and mutations
 * Includes 30-second polling for real-time updates
 */
export function useNotifications(options = {}) {
  const queryClient = useQueryClient();
  const { page = 1, limit = 20, type = null, unreadOnly = false } = options;

  // Fetch all notifications for user
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['notifications', { page, limit, type, unreadOnly }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (type) params.append('type', type);
      if (unreadOnly) params.append('unreadOnly', 'true');

      const response = await axiosInstance.get(
        `${API_ENDPOINTS.NOTIFICATIONS.GET_ALL}?${params.toString()}`
      );
      return response.data;
    },
    staleTime: 30000, // 30 seconds
    refetchInterval: 30000, // Poll every 30 seconds
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const notifications = data?.notifications || [];
  const pagination = data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 };
  const unreadCount = data?.unreadCount || 0;

  // Mark single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId) => {
      const response = await axiosInstance.put(
        API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId)
      );
      return response.data;
    },
    onSuccess: (_, notificationId) => {
      // Optimistically update the cache
      queryClient.setQueryData(['notifications', { page, limit, type, unreadOnly }], (old) => {
        if (!old) return old;
        return {
          ...old,
          notifications: old.notifications?.map((notification) =>
            notification.notificationId === notificationId
              ? { ...notification, read: true, readAt: new Date().toISOString() }
              : notification
          ),
          unreadCount: Math.max(0, (old.unreadCount || 1) - 1),
        };
      });
      // Also update unread count query
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      notify.error('Failed to mark notification as read');
    },
  });

  // Mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.put(
        API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.setQueryData(['notifications', { page, limit, type, unreadOnly }], (old) => {
        if (!old) return old;
        return {
          ...old,
          notifications: old.notifications?.map((notification) => ({
            ...notification,
            read: true,
            readAt: new Date().toISOString(),
          })),
          unreadCount: 0,
        };
      });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] });
      notify.success('All notifications marked as read');
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      notify.error('Failed to mark all notifications as read');
    },
  });

  // Delete notification
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId) => {
      const response = await axiosInstance.delete(
        API_ENDPOINTS.NOTIFICATIONS.DELETE(notificationId)
      );
      return response.data;
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      const previousData = queryClient.getQueryData(['notifications', { page, limit, type, unreadOnly }]);

      queryClient.setQueryData(['notifications', { page, limit, type, unreadOnly }], (old) => {
        if (!old) return old;
        const deletedNotif = old.notifications?.find(n => n.notificationId === notificationId);
        return {
          ...old,
          notifications: old.notifications?.filter(
            (notification) => notification.notificationId !== notificationId
          ),
          unreadCount: deletedNotif && !deletedNotif.read
            ? Math.max(0, (old.unreadCount || 1) - 1)
            : old.unreadCount,
        };
      });

      return { previousData };
    },
    onError: (err, notificationId, context) => {
      queryClient.setQueryData(
        ['notifications', { page, limit, type, unreadOnly }],
        context?.previousData
      );
      notify.error('Failed to delete notification');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    notifications,
    pagination,
    unreadCount,
    isLoading,
    error,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteNotification: deleteNotificationMutation.mutate,
    isMarkingRead: markAsReadMutation.isPending,
    isMarkingAllRead: markAllAsReadMutation.isPending,
    isDeletingNotification: deleteNotificationMutation.isPending,
    refetch,
  };
}

/**
 * Hook to get just the unread count (for navbar badge)
 * Uses shorter polling interval
 */
export function useUnreadCount() {
  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'unreadCount'],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT
      );
      return response.data;
    },
    staleTime: 15000, // 15 seconds
    refetchInterval: 30000, // Poll every 30 seconds
    refetchOnWindowFocus: true,
    retry: 1,
  });

  return {
    unreadCount: data?.unreadCount || 0,
    isLoading,
  };
}

/**
 * Hook for notification preferences
 */
export function useNotificationPreferences() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API_ENDPOINTS.NOTIFICATIONS.GET_PREFERENCES
      );
      return response.data;
    },
    staleTime: 60000, // 1 minute
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: async (preferences) => {
      const response = await axiosInstance.put(
        API_ENDPOINTS.NOTIFICATIONS.UPDATE_PREFERENCES,
        { preferences }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['notifications', 'preferences'], data);
      notify.success('Preferences updated');
    },
    onError: () => {
      notify.error('Failed to update preferences');
    },
  });

  return {
    preferences: data?.preferences || {},
    isLoading,
    updatePreferences: updatePreferencesMutation.mutate,
    isUpdating: updatePreferencesMutation.isPending,
  };
}
